/**
 * PlansContext - Provides subscription plan data from DynamoDB throughout the app.
 * Fetches plans from the public API on mount and caches them.
 * This is the single source of truth for all plan display data.
 *
 * When the API is unavailable, `error` is set and `plans` remains empty.
 * Consumer components should check `error` and render an error+retry UI
 * instead of pricing sections.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { plansApi } from '@/services/plansApi';
import { createLogger } from '@/lib/logger';
import type { Plan } from '@/types/plans';

const log = createLogger('PlansContext');

/** Map locale to currency code */
const LOCALE_CURRENCY_MAP: Record<string, string> = {
  'en-GB': 'GBP',
  'en-US': 'USD',
};

interface PlansContextValue {
  /** Whether plans are still being fetched */
  isLoading: boolean;
  /** Error message if plans failed to load, null otherwise */
  error: string | null;
  /** All active plans sorted by sortOrder (empty if API failed) */
  plans: Plan[];
  /** Get a specific plan by ID */
  getPlan: (planId: string) => Plan | undefined;
  /** Get the formatted price for a plan in the current locale's currency */
  getPlanPrice: (planId: string) => string;
  /** Get the header line for a plan in the current locale's currency */
  getPlanHeaderLine: (planId: string) => string;
  /** Current currency code based on locale */
  currency: string;
  /** Refresh plans from the API */
  refreshPlans: () => Promise<void>;
}

const PlansContext = createContext<PlansContextValue | null>(null);

export const PlansProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);

  const locale = import.meta.env.REACT_APP_LOCALE || 'en-GB';
  const currency = LOCALE_CURRENCY_MAP[locale] || 'GBP';

  const fetchPlans = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await plansApi.listPlans();
      // Ensure every plan has a display object (may be missing from older DynamoDB records)
      const normalized = data.map(plan => ({
        ...plan,
        display: plan.display || { icon: 'user', colorTheme: 'slate', recommended: false },
      }));
      setPlans(normalized);
      log.info('Plans loaded from API', { count: normalized.length });
    } catch (err) {
      log.error('Failed to fetch plans', err instanceof Error ? err : new Error(String(err)));
      setError('Unable to load plans. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const getPlan = useCallback(
    (planId: string): Plan | undefined => {
      return plans.find(p => p.planId === planId);
    },
    [plans]
  );

  const getPlanPrice = useCallback(
    (planId: string): string => {
      const plan = plans.find(p => p.planId === planId);
      if (!plan) return '';
      return plan.pricing[currency]?.formatted || plan.pricing['GBP']?.formatted || '';
    },
    [plans, currency]
  );

  const getPlanHeaderLine = useCallback(
    (planId: string): string => {
      const plan = plans.find(p => p.planId === planId);
      if (!plan) return '';
      return plan.pricing[currency]?.headerLine || plan.pricing['GBP']?.headerLine || '';
    },
    [plans, currency]
  );

  const value = useMemo<PlansContextValue>(
    () => ({
      isLoading,
      error,
      plans,
      getPlan,
      getPlanPrice,
      getPlanHeaderLine,
      currency,
      refreshPlans: fetchPlans,
    }),
    [isLoading, error, plans, getPlan, getPlanPrice, getPlanHeaderLine, currency, fetchPlans]
  );

  return (
    <PlansContext.Provider value={value}>
      {children}
    </PlansContext.Provider>
  );
};

export function usePlans(): PlansContextValue {
  const context = useContext(PlansContext);
  if (!context) {
    throw new Error('usePlans must be used within a PlansProvider');
  }
  return context;
}

export default PlansContext;
